import { V1RuleSet } from 'next-gen-sdk'
import { ApiFactory } from '../apiFactory'
import { createRuleSet } from './createRuleSet'
import {
  hasRuleSetChanged,
  compareRulesArrays,
  hasNestedRuleSetChanged,
} from '../Utils/ruleSetComparison'

/**
 * Updates a rule set and its nested rules/rule sets with optimized change detection.
 *
 * This function implements intelligent change detection to avoid unnecessary API calls
 * when rule sets haven't actually changed. It compares the incoming rule set against
 * the original to determine what needs to be updated, created, or can be skipped.
 *
 * Key optimizations:
 * - Skips entire update if no changes detected
 * - Only updates rules that have actually changed
 * - Only updates rule set properties when they've changed
 * - Recursively applies change detection to nested rule sets
 * - Always creates new rules (those without IDs)
 *
 * @param incomingRuleSet - The modified rule set to update
 * @param pixelId - The pixel ID for the API calls
 * @param subscriptionId - The subscription ID for the API calls
 * @param orgId - The organization ID for the API calls
 * @param originalRuleSet - The original rule set for comparison (optional for backward compatibility)
 * @returns The updated parent rule set or undefined if no parent rule set ID
 *
 * @example
 * ```typescript
 * // Update with change detection
 * const updated = await updateRuleSet(
 *   modifiedRuleSet,
 *   'pixel-123',
 *   'sub-456',
 *   'org-789',
 *   originalRuleSet // Enables change detection
 * )
 *
 * // Backward compatible usage (no change detection)
 * const updated = await updateRuleSet(
 *   modifiedRuleSet,
 *   'pixel-123',
 *   'sub-456',
 *   'org-789'
 * )
 * ```
 */
export const updateRuleSet = async (
  incomingRuleSet: V1RuleSet,
  pixelId: string,
  subscriptionId: string,
  orgId: string,
  originalRuleSet?: V1RuleSet,
) => {
  const token = localStorage.getItem('eltoro_token')
  const apis = ApiFactory(token)
  const { webToHomeAPI } = apis
  if (!webToHomeAPI) return undefined

  // Early return if no changes detected
  if (originalRuleSet && !hasRuleSetChanged(originalRuleSet, incomingRuleSet)) {
    console.log('No changes detected in rule set, skipping update')
    return originalRuleSet
  }

  const processRuleSet = async (
    ruleSet: V1RuleSet,
    originalRuleSetForComparison?: V1RuleSet,
  ) => {
    // ===== RULES PROCESSING WITH CHANGE DETECTION =====
    if (ruleSet.rules?.length) {
      const originalRules = originalRuleSetForComparison?.rules || []
      const rulesComparison = compareRulesArrays(originalRules, ruleSet.rules)

      // Optimization: Only process rules that actually need API calls
      // - New rules (no ID): Always create via API
      // - Changed rules (existing ID + different content): Update via API
      // - Unchanged rules: Skip entirely (no API call)
      const rulesToProcess = [
        ...rulesComparison.newRules, // Always create new rules
        ...rulesComparison.changedRules, // Only update changed rules
      ]

      if (rulesToProcess.length > 0) {
        await Promise.allSettled(
          rulesToProcess.map(async (rule) => {
            if (!rule.id) {
              // Create new rule: No ID means this is a brand new rule
              return webToHomeAPI.advertisingPlatformServiceCreateRule(
                pixelId,
                subscriptionId,
                ruleSet.id || '',
                { ...rule, orgId },
                orgId,
              )
            }
            // Update existing rule: Has ID and was detected as changed
            return webToHomeAPI.advertisingPlatformServiceUpdateRule(
              pixelId,
              subscriptionId,
              rule.ruleSetId || '',
              rule.id!,
              { ...rule, orgId },
              'minimum,maximum,term,type',
              orgId,
            )
          }),
        )
      }
    }

    // ===== RULE SET PROPERTIES UPDATE WITH CHANGE DETECTION =====
    if (ruleSet.id) {
      let shouldUpdateRuleSet = false

      if (originalRuleSetForComparison) {
        // Only update if the operator actually changed
        // This prevents unnecessary API calls for unchanged rule sets
        shouldUpdateRuleSet =
          originalRuleSetForComparison.operator !== ruleSet.operator
      } else {
        // No original to compare against, update the rule set
        shouldUpdateRuleSet = true
      }

      if (shouldUpdateRuleSet) {
        await webToHomeAPI
          .advertisingPlatformServiceUpdateRuleSet(
            pixelId,
            subscriptionId,
            ruleSet.id,
            {
              ...ruleSet,
              subscriptionId,
            },
            'operator',
            orgId,
          )
          .catch((e) => {
            console.error('Error updating rule set:', e)
          })
      }
    }

    // ===== NESTED RULE SETS PROCESSING WITH RECURSIVE CHANGE DETECTION =====
    if (ruleSet.ruleSet?.length) {
      const originalNestedRuleSets = originalRuleSetForComparison?.ruleSet || []

      // Optimization: Check if ANY nested rule sets have changed before processing
      // This prevents unnecessary processing of entire nested structures
      const hasNestedChanges = hasNestedRuleSetChanged(
        originalNestedRuleSets,
        ruleSet.ruleSet,
      )

      if (hasNestedChanges) {
        await Promise.allSettled(
          ruleSet.ruleSet.map(async (childRuleSet) => {
            if (!childRuleSet.id) {
              // Create new nested rule set: No ID means brand new
              return createRuleSet(
                { ...childRuleSet, parentRuleSetId: ruleSet.id },
                pixelId,
                subscriptionId,
                orgId,
              )
            }

            // Find corresponding original nested rule set for comparison
            // This enables change detection to work recursively down the tree
            const originalChildRuleSet = originalNestedRuleSets.find(
              (orig) => orig.id === childRuleSet.id,
            )

            // Recursively process the nested rule set with its own change detection
            // This applies the same optimization logic to deeply nested structures
            return processRuleSet(
              {
                ...childRuleSet,
                parentRuleSetId: ruleSet.id,
              },
              originalChildRuleSet, // Pass original for recursive change detection
            )
          }),
        )
      }
    }
  }

  await processRuleSet(incomingRuleSet, originalRuleSet)

  if (!incomingRuleSet.parentRuleSetId) {
    const parentRuleSet = await webToHomeAPI.advertisingPlatformServiceGetRuleSet(
      pixelId,
      subscriptionId,
      incomingRuleSet.id!,
      orgId,
    )
    return parentRuleSet
  }
  return undefined
}
